// Turbo en vivo: el mapa del coordinador de Turbo. Una sola pantalla con las
// tiendas que pueden tener Turbo (gris sin Turbo, verde con uno abierto, rojo
// cuando pasó a administración), los choferes de Turbo como moto o auto (azul
// esperando, naranja yendo a retirar, verde entregando), los anillos y el
// chofer ofrecido cuando entra un Turbo, la asignación a mano desde la misma
// tienda, y al costado la lista simple de los Turbos del día. Se refresca sola.
//
// Los datos vienen de GET /v2/turbo/live (tiendas, Turbos, ofertas, choferes
// y anillos en una sola llamada); el mapa es Leaflet (hooks/useLeafletMap).

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";
import { useLeafletMap } from "../hooks/useLeafletMap";
import { usePolledJson } from "../hooks/usePolledJson";
import type { LatLng, LGroup } from "../lib/leafletTypes";
import { TURBO_ESTADO_COLOR, TURBO_ESTADO_LABEL, TurboEstado, TurboHoy } from "../lib/mapLayers";
import { textTooltip } from "../lib/leafletText";
import { selfieSegura } from "../lib/selfie";
import { confirmarConTurboApagado } from "../lib/turboApagado";
import { bip, desbloquearSonido, EstadoSonido, estadoSonido } from "../lib/sonido";
import { waNumero } from "../lib/waNumero";
import {
  COLOR, despachoVivoDe, Driver, DRIVER_ESTADO, esc, escucharPopup, fmtHoraTurbo, fmtTime, htmlTienda, minutosDesde, nombre, pedidoAsignar, refrescarPopup, SIN_SENAL_MIN, Store, STORE_ESTADO,
} from "../lib/turboPopup";

type Offer = { route_id: number; delivery_id: number; store_id: number | null; driver_id: number; ring: number; source: string; response: string | null; offered_at: string; expires_at: string; responded_at: string | null; distance_km: string | number | null };
type Ring = { radius_km: number; ring_size: number; offer_timeout_seconds?: number };
type Live = { now: string; date: string; ultimo_dato_ml: string | null; config: { rings: Ring[]; store_driver_radius_km: number | null; position_max_age_minutes: number | null }; stores: Store[]; turbos: TurboHoy[]; resumen: Record<string, number>; offers: Offer[]; drivers: Driver[] };

const REFRESCO_MS = 10_000;
const RESALTE_MS = 8_000;
const MAPA = { center: [-34.615, -58.44] as LatLng, zoom: 12 };

const ORDEN_ESTADOS: TurboEstado[] = ["SIN_CHOFER", "CON_CHOFER", "EN_CAMINO", "NO_ENTREGADO", "ENTREGADO", "CANCELADO"];

const titleStyle: React.CSSProperties = { margin: 0, fontSize: "24px", fontWeight: 700, color: "var(--text-primary)" };
const subtitleStyle: React.CSSProperties = { margin: "8px 0 0 0", color: "var(--text-secondary)", fontSize: "15px", lineHeight: 1.6 };
const smallMutedStyle: React.CSSProperties = { fontSize: "12px", color: "var(--text-secondary)" };
const kpiStyle: React.CSSProperties = { fontSize: "26px", fontWeight: 800, lineHeight: 1.1, color: "var(--text-primary)", fontVariantNumeric: "tabular-nums" };
const chip = (on: boolean, color?: string): React.CSSProperties => ({ padding: "5px 11px", borderRadius: "999px", fontSize: "13px", fontWeight: 600, cursor: "pointer", border: `1px solid ${on ? (color ?? "var(--primary, #4f8cff)") : "var(--border)"}`, color: on ? (color ?? "var(--primary, #4f8cff)") : "var(--text-secondary)", background: "transparent" });
const badge = (color: string): React.CSSProperties => ({ display: "inline-block", padding: "2px 9px", borderRadius: "999px", fontSize: "12px", fontWeight: 600, border: `1px solid ${color}`, color, whiteSpace: "nowrap" });
const punto = (color: string): React.CSSProperties => ({ display: "inline-block", width: "10px", height: "10px", borderRadius: "50%", background: color, marginRight: "6px", verticalAlign: "middle" });
const filaStyle: React.CSSProperties = { display: "grid", gridTemplateColumns: "52px 1fr auto", gap: "10px", alignItems: "start", padding: "8px 6px", borderBottom: "1px solid var(--border)", cursor: "pointer", borderRadius: "8px", transition: "background 600ms ease" };
const linkBtnStyle: React.CSSProperties = { background: "none", border: "none", padding: 0, font: "inherit", fontSize: "12px", color: "var(--primary, #4f8cff)", cursor: "pointer", textDecoration: "underline" };

const fmtMin = (s: number) => { const m = Math.round(Math.abs(s) / 60); return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60} min`; };
const haceCuanto = (iso: string | null, now: number) => { if (!iso) return ""; const s = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000)); return s < 60 ? `hace ${s} s` : `hace ${fmtMin(s)}`; };
// «hace N s» que se actualiza solo cada segundo, sin re-renderizar la página
// (antes un reloj de 1 s re-renderizaba todo). `desfase`: lo que el reloj del
// servidor le lleva al de la PC, en ms (0 para una hora tomada en la PC).
function Hace({ iso, desfase }: { iso: string | null; desfase: number }) {
  const [ahora, setAhora] = useState(() => Date.now());
  useEffect(() => { const t = window.setInterval(() => setAhora(Date.now()), 1000); return () => window.clearInterval(t); }, []);
  return <>{haceCuanto(iso, ahora + desfase)}</>;
}

const leerPref = (k: string, def: boolean) => { try { const v = window.localStorage.getItem(k); return v == null ? def : v === "1"; } catch { return def; } };
const guardarPref = (k: string, v: boolean) => { try { window.localStorage.setItem(k, v ? "1" : "0"); } catch { /* nada */ } };

/* ===== Íconos (SVG en línea dentro de un divIcon) ===== */

const CSS = `
.tv-ico{position:relative;width:34px;height:34px;display:grid;place-items:center;filter:drop-shadow(0 1px 2px rgba(0,0,0,.6))}
.tv-ico svg{width:30px;height:30px;overflow:visible}
.tv-ico .n{position:absolute;top:-6px;right:-6px;min-width:18px;height:18px;padding:0 5px;border-radius:999px;background:#111;color:#fff;border:2px solid #fff;font:700 11px system-ui,sans-serif;display:grid;place-items:center}
.tv-ico .lbl{position:absolute;top:32px;left:50%;transform:translateX(-50%);white-space:nowrap;font:600 11px system-ui,sans-serif;color:#fff;background:rgba(12,14,18,.85);padding:1px 6px;border-radius:6px}
.tv-halo svg{filter:drop-shadow(0 0 6px ${COLOR.violeta}) drop-shadow(0 0 2px ${COLOR.violeta})}
.tv-pulse::before{content:"";position:absolute;inset:-6px;border-radius:50%;border:3px solid ${COLOR.rojo};animation:tvp 1.2s ease-out infinite}
.tv-ofer::before{content:"";position:absolute;inset:-6px;border-radius:50%;border:3px solid ${COLOR.violeta};animation:tvp 1.2s ease-out infinite}
@keyframes tvp{0%{transform:scale(.7);opacity:.9}100%{transform:scale(1.5);opacity:0}}
.tv-popup .leaflet-popup-content{margin:12px 14px;font:13px system-ui,sans-serif;min-width:260px}
.tv-popup select{width:100%;margin:6px 0;padding:6px 8px;border-radius:8px}
.tv-popup button{font:600 12px system-ui,sans-serif;padding:6px 10px;border-radius:8px;border:1px solid #4f8cff;background:#4f8cff;color:#fff;cursor:pointer;margin-right:6px;margin-top:4px}
.tv-popup button.sec{background:transparent;color:#4f8cff}
.tv-popup .t{padding:8px 0;border-top:1px solid rgba(128,128,128,.3)}
.tv-popup .m{font-size:12px;opacity:.8}
.tv-ocupado .tv-popup button{opacity:.5;pointer-events:none}
@media (prefers-reduced-motion: reduce){.tv-pulse::before,.tv-ofer::before{animation:none}}
`;

const SVG_TIENDA = (color: string) => `<svg viewBox="0 0 24 24"><path d="M3 10 4.2 4h15.6L21 10z" fill="${color}" stroke="#111" stroke-width="1.2"/><path d="M5 10v10h14V10" fill="${color}" stroke="#111" stroke-width="1.2"/><path d="M10 20v-6h4v6" fill="#111" opacity=".55"/></svg>`;
const SVG_MOTO = (color: string) => `<svg viewBox="0 0 24 24"><circle cx="6" cy="17" r="3.2" fill="${color}" stroke="#111" stroke-width="1.2"/><circle cx="18" cy="17" r="3.2" fill="${color}" stroke="#111" stroke-width="1.2"/><path d="M6 17l3.5-7h4.5l2-3h3.5M14 10l4 7M9.5 10h6" fill="none" stroke="${color}" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M6 17l3.5-7h4.5l2-3h3.5M14 10l4 7" fill="none" stroke="#111" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" opacity=".5"/></svg>`;
const SVG_AUTO = (color: string) => `<svg viewBox="0 0 24 24"><path d="M3 14l2.2-5.2A1.5 1.5 0 0 1 6.6 8h10.8a1.5 1.5 0 0 1 1.4.8L21 14v4.5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" fill="${color}" stroke="#111" stroke-width="1.2"/><path d="M5.5 13.5h13" stroke="#111" stroke-width="1" opacity=".5"/><circle cx="7" cy="19" r="2.2" fill="#111"/><circle cx="17" cy="19" r="2.2" fill="#111"/></svg>`;

const iconoTienda = (L: any, s: Store) => L.divIcon({
  className: "", iconSize: [34, 34], iconAnchor: [17, 17], popupAnchor: [0, -14],
  html: `<div class="tv-ico ${s.estado === "ESCALADO" ? "tv-pulse" : ""}" title="${esc(s.name)}">${SVG_TIENDA(STORE_ESTADO[s.estado].color)}${s.turbos.abiertos ? `<span class="n">${s.turbos.abiertos}</span>` : ""}<span class="lbl">${esc(s.name)}</span></div>`,
});
const iconoChofer = (L: any, d: Driver, color: string, ofrecido: boolean) => L.divIcon({
  className: "", iconSize: [34, 34], iconAnchor: [17, 17],
  html: `<div class="tv-ico ${ofrecido ? "tv-ofer tv-halo" : ""}" title="${esc(nombre(d))}">${d.tipo === "moto" ? SVG_MOTO(color) : SVG_AUTO(color)}${d.turbos_abiertos > 1 ? `<span class="n">${d.turbos_abiertos}</span>` : ""}<span class="lbl">${esc(nombre(d).split(" ")[0])}</span></div>`,
});

type Grupos = { rings: LGroup; lines: LGroup; stores: LGroup; drivers: LGroup };

/* ===== Ficha del chofer: quién es, cómo contactarlo y qué hizo hoy ===== */

type DiaChofer = {
  driver: { driver_id: number; username: string; first_name: string | null; last_name: string | null; phone: string | null; vehiculo: string | null; patente: string | null; turbo_enabled: boolean; turbo_active: boolean; selfie: string | null; tipo: "moto" | "auto" };
  posicion: { latitude: number; longitude: number; recorded_at: string; source: string; battery_level: number | null; accuracy_m: number | null } | null;
  posiciones: { n: number; primera: string | null; ultima: string | null; km: string | number };
  recorridos: { id: number; type: string; status: string; started_at: string | null; completed_at: string | null; notes: string | null; paradas: number }[];
  turbos: TurboHoy[];
  ofertas: { route_id: number; ring: number; source: string; response: string | null; offered_at: string; expires_at: string; responded_at: string | null; distance_km: string | number | null; store_name: string | null; nombre_destinatario: string | null }[];
  resumen_ofertas: { total: number; aceptadas: number; rechazadas: number; sin_respuesta: number; superadas: number };
  eventos: { at: string; from_status: string | null; to_status: string; source: string; notes: string | null; route_id: number | null; tracking: string | number | null; nombre_destinatario: string | null; store_name: string | null }[];
};
const OPS_LABEL: Record<string, string> = {
  ENTERED: "ingresó", COLLECTED: "retiró en la tienda", ARRIVED_WAREHOUSE: "llegó a planta", SHELVED: "estanteó", ASSIGNED_TO_ROUTE: "quedó en recorrido",
  OUT_FOR_DELIVERY: "salió a entregar", DELIVERED: "entregó", NOT_DELIVERED: "no entregó", CANCELLED: "se canceló",
};
// Por qué el despacho forzado no ofreció (skipped de POST /v2/turbo/dispatches).
const SALTEADO: Record<string, string> = {
  DISABLED: "el módulo Turbo está apagado", NOT_TURBO: "el envío no es Turbo", CLOSED: "el envío ya está cerrado",
  ALREADY_DISPATCHED: "ya tiene un despacho", ALREADY_ROUTED: "ya está en otro recorrido", STORE_NO_LOCATION: "la tienda no tiene ubicación cargada",
  OUT_OF_WINDOW: "está fuera de franja", OUT_OF_RADIUS: "está fuera del radio",
};
const RESP_LABEL: Record<string, string> = { ACCEPTED: "aceptó", DECLINED: "pasó", EXPIRED: "no contestó", SUPERSEDED: "lo tomó otro" };
const ROUTE_TYPE: Record<string, string> = { TURBO: "Turbo", PICKUP: "colecta", DELIVERY: "reparto" };
const ROUTE_ST: Record<string, string> = { PLANNED: "planificado", ASSIGNED: "asignado", LOADED: "cargado", IN_PROGRESS: "en curso", COMPLETED: "terminado", CANCELLED: "cancelado" };

const iniciales = (d: { first_name: string | null; last_name: string | null; username: string }) =>
  ((d.first_name?.[0] ?? "") + (d.last_name?.[0] ?? "")).toUpperCase() || d.username.slice(0, 2).toUpperCase();

function FichaChofer({ driverId, live, desfase, req, onCerrar, onAvisar, avisando }: {
  driverId: number; live: Live | null; desfase: number; req: (p: string, i?: RequestInit) => Promise<any>; onCerrar: () => void; onAvisar: (id: number) => void; avisando: boolean;
}) {
  const [dia, setDia] = useState<DiaChofer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [verTodo, setVerTodo] = useState(false);
  useEffect(() => {
    let vivo = true;
    const cargar = () => req(`/v2/turbo/live/driver/${driverId}`).then((j) => { if (vivo) { setDia(j); setError(null); } }).catch((e) => { if (vivo) setError(e.message); });
    setDia(null); cargar();
    const t = window.setInterval(cargar, 30_000);
    return () => { vivo = false; window.clearInterval(t); };
  }, [driverId, req]);

  const enVivo = live?.drivers.find((d) => d.driver_id === driverId) ?? null;
  const d = dia?.driver;
  const estado = enVivo ? DRIVER_ESTADO[enVivo.estado] : null;
  const pos = dia?.posicion;
  // Con el reloj del servidor: se recalcula con cada sondeo de la pantalla.
  const ahora = Date.now() + desfase;
  const sinSenal = pos ? minutosDesde(pos.recorded_at, ahora) > SIN_SENAL_MIN : true;
  const color = enVivo && enVivo.turbo_route_id != null ? estado!.color : sinSenal ? COLOR.gris : (estado?.color ?? COLOR.gris);
  const nombreD = d ? nombre(d) : (enVivo ? nombre(enVivo) : `chofer ${driverId}`);
  const turboActual = dia?.turbos.find((t) => !t.terminal) ?? null;
  const waTexto = `Hola ${d?.first_name ?? ""}, te escribo desde Lynx.${turboActual ? ` Es por el Turbo de ${turboActual.store_name ?? "la tienda"} para ${turboActual.nombre_destinatario ?? "el comprador"}.` : ""}`;
  const tel = d?.phone ?? enVivo?.phone ?? null;
  const btn = (bg: string, fg = "#fff"): React.CSSProperties => ({ display: "inline-flex", alignItems: "center", gap: "6px", padding: "7px 12px", borderRadius: "10px", border: `1px solid ${bg}`, background: bg, color: fg, fontSize: "13px", fontWeight: 600, textDecoration: "none", cursor: "pointer" });
  const col: React.CSSProperties = { minWidth: 0 };
  const h: React.CSSProperties = { ...smallMutedStyle, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: "4px" };
  const eventos = dia?.eventos ?? [];

  return (
    <div role="region" aria-label={`Ficha de ${nombreD}`} style={{ border: `1px solid ${color}`, borderRadius: "14px", padding: "12px 14px", marginBottom: "10px", background: "rgba(79,140,255,0.06)" }}>
      <div style={{ display: "flex", gap: "14px", alignItems: "center", flexWrap: "wrap" }}>
        {d && selfieSegura(d.selfie)
          ? <img src={d.selfie} alt={nombreD} style={{ width: "56px", height: "56px", borderRadius: "50%", objectFit: "cover", border: `3px solid ${color}` }} />
          : <div aria-hidden="true" style={{ width: "56px", height: "56px", borderRadius: "50%", display: "grid", placeItems: "center", background: color, color: "#111", fontWeight: 800, fontSize: "20px" }}>{d ? iniciales(d) : "…"}</div>}
        <div style={{ flex: 1, minWidth: "220px" }}>
          <div style={{ fontSize: "17px", fontWeight: 700, color: "var(--text-primary)" }}>
            {nombreD} <span style={{ ...smallMutedStyle, fontWeight: 400 }}>{d?.username ?? enVivo?.username}</span>
          </div>
          <div style={{ ...smallMutedStyle, marginTop: "2px" }}>
            <span style={{ display: "inline-block", width: "18px", height: "18px", verticalAlign: "middle", marginRight: "4px" }} dangerouslySetInnerHTML={{ __html: (d?.tipo ?? enVivo?.tipo) === "moto" ? SVG_MOTO(color) : SVG_AUTO(color) }} />
            {d?.vehiculo ?? enVivo?.vehiculo ?? "vehículo sin cargar"}{d?.patente ? ` · ${d.patente}` : ""}
            {d ? (d.turbo_enabled ? " · habilitado para Turbo" : " · NO habilitado para Turbo") : ""}
            {d ? (d.turbo_active ? " · interruptor prendido" : " · interruptor apagado") : ""}
          </div>
          <div style={{ marginTop: "4px" }}>
            {estado ? <span style={badge(color)}>{sinSenal && enVivo?.turbo_route_id == null ? "sin señal" : estado.label}</span> : <span style={badge(COLOR.gris)}>sin posición hoy</span>}
            {pos ? <span style={{ ...smallMutedStyle, marginLeft: "8px" }}>reportó <Hace iso={pos.recorded_at} desfase={desfase} />{pos.battery_level != null ? ` · batería ${Math.round(pos.battery_level * 100)}%` : ""}</span> : null}
            {enVivo?.turbo_route_id ? <span style={{ ...smallMutedStyle, marginLeft: "8px" }}>· Turbo #{enVivo.turbo_route_id}{enVivo.turbos_abiertos > 1 ? ` (+${enVivo.turbos_abiertos - 1})` : ""}</span> : null}
          </div>
        </div>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
          {tel ? (
            <>
              <a href={`https://wa.me/${waNumero(tel)}?text=${encodeURIComponent(waTexto)}`} target="_blank" rel="noreferrer" style={btn("#25D366", "#0b2e13")}>WhatsApp</a>
              <a href={`tel:${tel.replace(/\D/g, "")}`} style={btn("transparent", "var(--text-primary)")}>Llamar {tel}</a>
            </>
          ) : <span style={smallMutedStyle}>sin teléfono cargado</span>}
          <button type="button" style={btn("transparent", "var(--text-primary)")} disabled={avisando} onClick={() => onAvisar(driverId)}>{avisando ? "avisando…" : "Avisar por la app"}</button>
          <button type="button" aria-label="Cerrar ficha" onClick={onCerrar} style={{ ...btn("transparent", "var(--text-secondary)"), fontSize: "18px", padding: "4px 10px" }}>×</button>
        </div>
      </div>
      {error ? <div style={{ ...smallMutedStyle, color: "var(--danger)", marginTop: "8px" }}>{error}</div> : null}
      {dia ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px", marginTop: "12px" }}>
          <div style={col}>
            <div style={h}>Turbos de hoy · {dia.turbos.length}</div>
            {dia.turbos.length === 0 ? <div style={smallMutedStyle}>Ninguno todavía.</div> : dia.turbos.map((t) => (
              <div key={t.delivery_id} style={{ ...smallMutedStyle, padding: "4px 0", borderBottom: "1px solid var(--border)" }}>
                <div style={{ color: "var(--text-primary)" }}><b>{fmtHoraTurbo(t, t.entro_at)}</b> {t.store_name} → {t.nombre_destinatario} <span style={badge(TURBO_ESTADO_COLOR[t.estado])}>{TURBO_ESTADO_LABEL[t.estado]}</span></div>
                <div>
                  {t.recorrido?.started_at ? `salió ${fmtTime(t.recorrido.started_at)}` : "no salió"}
                  {t.hitos.pickup_arrived_at ? ` · en la tienda ${fmtTime(t.hitos.pickup_arrived_at)}` : ""}
                  {t.hitos.collected_at ? ` · retiró ${fmtTime(t.hitos.collected_at)}` : ""}
                  {t.hitos.delivery_arrived_at ? ` · en el domicilio ${fmtTime(t.hitos.delivery_arrived_at)}` : ""}
                  {t.terminal ? ` · ${TURBO_ESTADO_LABEL[t.estado]} ${fmtTime(t.estado_at)}` : t.vence_at ? ` · vence ${fmtHoraTurbo(t, t.vence_at)}` : ""}
                </div>
              </div>
            ))}
          </div>
          <div style={col}>
            <div style={h}>Hoy</div>
            <div style={smallMutedStyle}>
              <div><b style={{ color: "var(--text-primary)" }}>Ofertas:</b> recibió {dia.resumen_ofertas.total} · aceptó {dia.resumen_ofertas.aceptadas} · pasó {dia.resumen_ofertas.rechazadas} · no contestó {dia.resumen_ofertas.sin_respuesta}{dia.resumen_ofertas.superadas ? ` · se las ganó otro ${dia.resumen_ofertas.superadas}` : ""}</div>
              <div><b style={{ color: "var(--text-primary)" }}>GPS:</b> {dia.posiciones.n ? `desde ${fmtTime(dia.posiciones.primera)} hasta ${fmtTime(dia.posiciones.ultima)} · ${dia.posiciones.n} reportes · unos ${dia.posiciones.km} km` : "sin posiciones hoy"}</div>
              <div><b style={{ color: "var(--text-primary)" }}>Recorridos:</b> {dia.recorridos.length ? dia.recorridos.map((r) => `#${r.id} ${ROUTE_TYPE[r.type] ?? r.type} ${ROUTE_ST[r.status] ?? r.status}`).join(" · ") : "ninguno"}</div>
            </div>
            {dia.ofertas.length ? (
              <div style={{ marginTop: "6px" }}>
                <div style={h}>Ofertas</div>
                {dia.ofertas.slice(0, verTodo ? 50 : 4).map((o, i) => (
                  <div key={i} style={smallMutedStyle}>{fmtTime(o.offered_at)} {o.store_name ?? "tienda"} → {o.nombre_destinatario ?? "destino"} · {o.source === "STORE" ? "como chofer de la tienda" : `anillo ${o.ring}`} · <b style={{ color: o.response === "ACCEPTED" ? COLOR.verde : o.response === "DECLINED" ? COLOR.rojo : "var(--text-secondary)" }}>{o.response ? RESP_LABEL[o.response] ?? o.response : (new Date(o.expires_at).getTime() > ahora ? "esperando respuesta" : "no contestó")}</b></div>
                ))}
              </div>
            ) : null}
          </div>
          <div style={col}>
            <div style={h}>Movimientos de hoy · {eventos.length}</div>
            {eventos.length === 0 ? <div style={smallMutedStyle}>Sin movimientos registrados.</div> : eventos.slice(0, verTodo ? 40 : 6).map((e, i) => (
              <div key={i} style={smallMutedStyle}><b style={{ color: "var(--text-primary)" }}>{fmtTime(e.at)}</b> {OPS_LABEL[e.to_status] ?? e.to_status} · {e.store_name ?? ""}{e.nombre_destinatario ? ` → ${e.nombre_destinatario}` : ""}{e.source === "system" ? " (automático)" : ""}</div>
            ))}
            {(eventos.length > 6 || dia.ofertas.length > 4) ? <button type="button" style={linkBtnStyle} onClick={() => setVerTodo((v) => !v)}>{verTodo ? "ver menos" : "ver todo"}</button> : null}
          </div>
        </div>
      ) : (!error ? <div style={{ ...smallMutedStyle, marginTop: "8px" }}>Cargando el día del chofer…</div> : null)}
    </div>
  );
}

export default function TurboVivoPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const req = useCallback(async (path: string, init?: RequestInit) => {
    const res = await authFetch(path, { ...(init ?? {}), token, json: init?.body !== undefined });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json?.ok === false) throw new Error(json?.error || `Error ${res.status}`);
    return json;
  }, [token]);

  const vivo = usePolledJson<Live>("/v2/turbo/live", REFRESCO_MS, token, (j) => j as Live);
  const data = vivo.data;
  const contenedorRef = useRef<HTMLDivElement>(null);
  const { ready: leaflet, leafletMap } = useLeafletMap<Grupos>(contenedorRef, MAPA, (L, map) => {
    const g = { rings: L.layerGroup(), lines: L.layerGroup(), stores: L.layerGroup(), drivers: L.layerGroup() };
    for (const k of ["rings", "lines", "stores", "drivers"] as const) g[k].addTo(map);
    return g;
  });

  const [sonido, setSonido] = useState(() => leerPref("turbo-vivo-sonido", false));
  // Lo que deja el navegador (ver lib/sonido): después de recargar, el sonido
  // guardado como prendido queda bloqueado hasta el primer toque.
  const [sonidoEstado, setSonidoEstado] = useState<EstadoSonido | null>(null);
  const [estadoFiltro, setEstadoFiltro] = useState<TurboEstado | "">("");
  const [ocultarCerrados, setOcultarCerrados] = useState(() => leerPref("turbo-vivo-ocultar", false));
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // La acción en curso («Asignando…», «Ofreciendo…»), o null.
  const [busy, setBusy] = useState<string | null>(null);
  // Asignación a mano «tocando un chofer en el mapa».
  const [modoAsignar, setModoAsignar] = useState<{ turbo: TurboHoy } | null>(null);
  // Chofer elegido (tocado en el mapa o en la lista): su ficha arriba del mapa.
  const [driverSel, setDriverSel] = useState<number | null>(null);
  const [avisando, setAvisando] = useState<number | null>(null);
  // Un aviso a la vez, con un solo timer: antes cada aviso dejaba el suyo y el
  // del anterior borraba al nuevo antes de tiempo.
  const avisoTimer = useRef<number | undefined>(undefined);
  const flash = useCallback((m: string) => {
    setNotice(m);
    window.clearTimeout(avisoTimer.current);
    avisoTimer.current = window.setTimeout(() => setNotice(null), 5000);
  }, []);
  useEffect(() => () => window.clearTimeout(avisoTimer.current), []);
  const avisarChofer = useCallback(async (driverId: number) => {
    setAvisando(driverId);
    try {
      const j = await req(`/v2/drivers/${driverId}/ping`, { method: "POST", body: JSON.stringify({}) });
      flash(j.sent ? `Aviso enviado a ${j.driver_name ?? "el chofer"}` : `${j.driver_name ?? "El chofer"} no tiene la app registrada${j.phone ? `: llamalo al ${j.phone}` : ""}`);
    } catch (e: any) { setError(e.message); } finally { setAvisando(null); }
  }, [req, flash]);
  const [resaltados, setResaltados] = useState<Record<number, { hasta: number; nuevo: boolean }>>({});
  const previos = useRef<Map<number, TurboEstado> | null>(null);
  const sonidoRef = useRef(sonido); sonidoRef.current = sonido;
  const dataRef = useRef<Live | null>(null); dataRef.current = data;
  // Lo que el reloj del servidor le lleva al de la PC (ms), medido en cada
  // sondeo: «hace…» y «sin señal» se cuentan con la hora del servidor (con la
  // PC atrasada, los choferes salían en gris o «hace 0 s» para siempre).
  const desfase = useMemo(() => {
    const t = data ? Date.parse(data.now) : NaN;
    return Number.isFinite(t) && vivo.updatedAt ? t - vivo.updatedAt.getTime() : 0;
  }, [data, vivo.updatedAt]);
  const desfaseRef = useRef(desfase); desfaseRef.current = desfase;
  const modoRef = useRef(modoAsignar); modoRef.current = modoAsignar;
  const marcadores = useRef<{ stores: Map<number, any>; drivers: Map<number, any> }>({ stores: new Map(), drivers: new Map() });
  const popupTienda = useRef<number | null>(null);
  // Una acción a la vez (popup, lista o clic en el mapa): el estado busy llega
  // tarde para un doble clic, la ref no.
  const ocupado = useRef(false);
  useEffect(() => { contenedorRef.current?.classList.toggle("tv-ocupado", !!busy); }, [busy]);

  // Cada resaltado vence solo: un timer hasta el próximo vencimiento (antes,
  // un reloj de 1 s re-renderizaba toda la página para esto).
  useEffect(() => {
    const hastas = Object.values(resaltados).map((v) => v.hasta);
    if (!hastas.length) return;
    const t = window.setTimeout(() => {
      const ahora = Date.now();
      setResaltados((p) => Object.fromEntries(Object.entries(p).filter(([, v]) => v.hasta > ahora)));
    }, Math.max(0, Math.min(...hastas) - Date.now()) + 50);
    return () => window.clearTimeout(t);
  }, [resaltados]);

  // Cambios entre una vuelta y la siguiente: resaltar y, si está prendido, sonar.
  useEffect(() => {
    if (!data) return;
    const ahora = Date.now();
    const cambios: Record<number, { hasta: number; nuevo: boolean }> = {};
    let hay = false;
    if (previos.current) {
      for (const t of data.turbos) {
        const antes = previos.current.get(t.delivery_id);
        if (antes === undefined) { cambios[t.delivery_id] = { hasta: ahora + RESALTE_MS * 2, nuevo: true }; hay = true; }
        else if (antes !== t.estado) { cambios[t.delivery_id] = { hasta: ahora + RESALTE_MS, nuevo: false }; hay = true; }
      }
    }
    previos.current = new Map(data.turbos.map((t) => [t.delivery_id, t.estado]));
    if (hay) { setResaltados((p) => ({ ...p, ...cambios })); if (sonidoRef.current) setSonidoEstado(bip()); }
  }, [data]);

  // Al entrar, con el sonido guardado como prendido: ¿el navegador lo deja
  // sonar? (Al prenderlo con el botón, lo resuelve el botón.)
  useEffect(() => { if (sonidoRef.current) setSonidoEstado(estadoSonido()); }, []);
  // Bloqueado: el primer toque en cualquier lado lo habilita (el del botón lo
  // maneja el botón, que si no lo apagaría en el mismo toque).
  useEffect(() => {
    if (!sonido || sonidoEstado !== "bloqueado") return;
    const habilitar = (ev: Event) => {
      if ((ev.target as Element | null)?.closest?.(".tv-sonido")) return;
      desbloquearSonido().then(setSonidoEstado);
    };
    document.addEventListener("pointerdown", habilitar);
    return () => document.removeEventListener("pointerdown", habilitar);
  }, [sonido, sonidoEstado]);
  const tocarSonido = () => {
    if (sonido && sonidoEstado === "bloqueado") { desbloquearSonido().then(setSonidoEstado); return; }
    const v = !sonido; setSonido(v); guardarPref("turbo-vivo-sonido", v);
    if (v) desbloquearSonido().then((e) => { setSonidoEstado(e); if (e === "listo") bip(); });
  };


  /* ===== acciones ===== */

  const asignar = useCallback(async (turbo: TurboHoy, driverId: number) => {
    if (ocupado.current) return;
    ocupado.current = true; setBusy("Asignando…"); setError(null);
    try {
      // Siempre la asignación a mano del Turbo: en una sola transacción asigna,
      // cierra las ofertas abiertas (si no, un chofer que aceptaba después se lo
      // quedaba) y no toca un envío que está en otro recorrido. Sin despacho
      // vivo, crea el recorrido. Va como «lo vi sin chofer» (ver
      // pedidoAsignar): si en el medio lo tomó alguien, el servidor no lo pisa.
      await req("/v2/turbo/dispatches/manual", { method: "POST", body: JSON.stringify(pedidoAsignar(turbo, driverId)) });
      const d = dataRef.current?.drivers.find((x) => x.driver_id === driverId);
      flash(`Turbo ${turbo.tracking ?? `#${turbo.delivery_id}`} asignado a ${d ? nombre(d) : `chofer ${driverId}`}`);
    } catch (e: any) { setError(e.message); } finally {
      // Salga bien o no (409: lo tomó otro o se cerró), lo elegido ya no vale:
      // sale del modo «elegir en el mapa», cierra el popup y recarga.
      setModoAsignar(null);
      (leafletMap?.map as any)?.closePopup?.();
      vivo.reload();
      ocupado.current = false; setBusy(null);
    }
  }, [req, vivo, leafletMap, flash]);
  const asignarRef = useRef(asignar); asignarRef.current = asignar;

  const reofertar = useCallback(async (turbo: TurboHoy) => {
    if (!turbo.despacho || ocupado.current) return;
    ocupado.current = true; setBusy("Reofertando…"); setError(null);
    try {
      if (!(await confirmarConTurboApagado(req, "Volver a ofrecerlo a los choferes"))) return;
      const r = await req(`/v2/turbo/dispatches/${turbo.despacho.route_id}/reoffer`, { method: "POST", body: JSON.stringify({}) });
      flash(r.escalated ? "No hay choferes disponibles cerca: sigue escalado" : `Ofrecido de nuevo a ${r.offers?.length ?? 0} chofer${(r.offers?.length ?? 0) === 1 ? "" : "es"}`);
      vivo.reload();
    } catch (e: any) { setError(e.message); } finally { ocupado.current = false; setBusy(null); }
  }, [req, vivo, flash]);
  const reofertarRef = useRef(reofertar); reofertarRef.current = reofertar;

  // Ofrecer a los choferes un Turbo que el automático salteó (o cuyo recorrido
  // se canceló): despacho forzado por anillos; si nadie queda cerca, escala.
  const ofrecer = useCallback(async (turbo: TurboHoy) => {
    if (ocupado.current) return;
    ocupado.current = true; setBusy("Ofreciendo…"); setError(null);
    try {
      if (!(await confirmarConTurboApagado(req, "Ofrecerlo a los choferes"))) return;
      const j = await req("/v2/turbo/dispatches", { method: "POST", body: JSON.stringify({ delivery_id: turbo.delivery_id, force: true }) });
      if (j.dispatched === false) throw new Error(`No se pudo ofrecer: ${SALTEADO[j.skipped] ?? j.skipped ?? "sin motivo"}${j.route_id ? ` (recorrido #${j.route_id})` : ""}`);
      const n = j.offers?.length ?? 0;
      flash(j.escalated ? "No hay choferes disponibles cerca: quedó escalado, asignalo a mano" : `Ofrecido a ${n} chofer${n === 1 ? "" : "es"}${(j.warnings ?? []).includes("OUT_OF_RADIUS") ? " (está fuera del radio)" : ""}${(j.warnings ?? []).includes("OUT_OF_WINDOW") ? " (fuera de franja)" : ""}`);
      (leafletMap?.map as any)?.closePopup?.();
      vivo.reload();
    } catch (e: any) { setError(e.message); } finally { ocupado.current = false; setBusy(null); }
  }, [req, vivo, leafletMap, flash]);
  const ofrecerRef = useRef(ofrecer); ofrecerRef.current = ofrecer;

  /* ===== popup de una tienda: sus Turbos y la asignación a mano (lib/turboPopup) ===== */

  // El contenido del popup de una tienda, con el último sondeo: el popup se ata
  // una sola vez al marcador y Leaflet lo pide al abrirlo y al rearmarlo.
  const contenidoTienda = useCallback((id: number) => {
    const live = dataRef.current;
    const s = live?.stores.find((x) => x.id === id);
    return live && s ? htmlTienda(s, live, Date.now() + desfaseRef.current) : "";
  }, []);

  const abrirTienda = useCallback((s: Store) => {
    marcadores.current.stores.get(s.id)?.openPopup();
  }, []);

  // Los botones del popup son HTML suelto: se escuchan por delegación en el mapa.
  useEffect(() => {
    const el = contenedorRef.current;
    if (!el) return;
    return escucharPopup(el, () => dataRef.current, () => ocupado.current, {
      asignar: (turbo, driverId) => asignarRef.current(turbo, driverId),
      elegir: (turbo) => { setModoAsignar({ turbo }); (leafletMap?.map as any)?.closePopup?.(); },
      ofrecer: (turbo) => ofrecerRef.current(turbo),
      reofertar: (turbo) => reofertarRef.current(turbo),
    });
  }, [leafletMap]);

  /* ===== dibujo ===== */

  useEffect(() => {
    if (!leafletMap || !data) return;
    const { L, extra: g } = leafletMap;
    const Lx = L as any;
    const ahora = Date.now() + desfaseRef.current;
    const offersVivas = data.offers.filter((o) => !o.response && new Date(o.expires_at).getTime() > ahora);
    const ofrecidos = new Set(offersVivas.map((o) => o.driver_id));
    const storeById = new Map(data.stores.map((s) => [s.id, s]));

    // Anillos y líneas: solo para los Turbos que se están ofreciendo ahora.
    g.rings.clearLayers(); g.lines.clearLayers();
    for (const t of data.turbos) {
      if (t.terminal || !t.tienda) continue;
      const centro: LatLng = [t.tienda.lat, t.tienda.lng];
      if (t.despacho?.status === "OFFERING") {
        const actual = t.despacho.ring ?? 1;
        data.config.rings.forEach((r, i) => {
          const esActual = i + 1 === actual;
          Lx.circle(centro, { radius: r.radius_km * 1000, color: COLOR.violeta, weight: esActual ? 2.5 : 1, opacity: esActual ? 0.9 : 0.35, fill: esActual, fillColor: COLOR.violeta, fillOpacity: 0.05, dashArray: esActual ? undefined : "4 8", interactive: false })
            .addTo(g.rings);
        });
        for (const o of offersVivas.filter((o) => o.route_id === t.despacho!.route_id)) {
          const d = data.drivers.find((x) => x.driver_id === o.driver_id);
          if (d) L.polyline([centro, [d.lat, d.lng]], { color: COLOR.violeta, weight: 2, dashArray: "6 6", opacity: 0.9 }).addTo(g.lines);
        }
      }
      // Chofer con el Turbo: de la tienda al chofer (naranja) y del chofer al destino (verde).
      if (t.chofer && (t.estado === "CON_CHOFER" || t.estado === "EN_CAMINO")) {
        const d = data.drivers.find((x) => x.driver_id === t.chofer!.driver_id);
        if (d && t.estado === "CON_CHOFER") L.polyline([[d.lat, d.lng], centro], { color: COLOR.naranja, weight: 2, dashArray: "2 6", opacity: 0.7 }).addTo(g.lines);
        if (d && t.estado === "EN_CAMINO" && t.destino) L.polyline([[d.lat, d.lng], [t.destino.lat, t.destino.lng]], { color: COLOR.verde, weight: 2, dashArray: "2 6", opacity: 0.7 }).addTo(g.lines);
      }
      if (!t.chofer && t.destino) {
        L.circleMarker([t.destino.lat, t.destino.lng], { radius: 5, weight: 1, color: "#111", fillColor: TURBO_ESTADO_COLOR[t.estado], fillOpacity: 0.9 }).addTo(g.lines);
        L.polyline([centro, [t.destino.lat, t.destino.lng]], { color: TURBO_ESTADO_COLOR[t.estado], weight: 1.5, dashArray: "3 6", opacity: 0.5 }).addTo(g.lines);
      }
    }

    // Tiendas: un marcador por tienda, que se actualiza sin recrearlo (conserva el popup abierto).
    const vivosT = new Set<number>();
    for (const s of data.stores) {
      vivosT.add(s.id);
      let m = marcadores.current.stores.get(s.id);
      const icono = iconoTienda(L, s);
      if (!m) {
        m = Lx.marker([s.lat, s.lng], { icon: icono, zIndexOffset: 200 }).addTo(g.stores);
        // El popup se ata una sola vez: Leaflet lo abre y lo cierra con cada
        // toque (atarlo en cada clic le sumaba su manejador y el segundo toque lo
        // cerraba). El contenido se arma al abrirlo, con el último sondeo.
        const id = s.id;
        m.bindPopup(() => contenidoTienda(id), { className: "tv-popup", maxWidth: 360, autoPan: true });
        m.on("popupopen", () => { popupTienda.current = id; });
        m.on("popupclose", () => { if (popupTienda.current === id) popupTienda.current = null; });
        marcadores.current.stores.set(s.id, m);
      } else {
        // Solo si se movió: setLatLng mueve el popup abierto y Leaflet lo
        // recentra (autoPan), así que en cada sondeo el mapa volvía solo.
        const ll = m.getLatLng();
        if (ll.lat !== s.lat || ll.lng !== s.lng) m.setLatLng([s.lat, s.lng]);
        // Solo si cambió: rehacer el ícono reinicia la animación de la tienda.
        if (m.options.icon?.options?.html !== icono.options.html) m.setIcon(icono);
      }
      if (popupTienda.current === s.id && m.isPopupOpen?.()) refrescarPopup(m.getPopup());
    }
    for (const [id, m] of marcadores.current.stores) if (!vivosT.has(id)) { (g.stores as any).removeLayer(m); marcadores.current.stores.delete(id); }

    // Choferes: moto o auto, del color de lo que están haciendo.
    const vivosD = new Set<number>();
    for (const d of data.drivers) {
      vivosD.add(d.driver_id);
      const sinSenal = minutosDesde(d.recorded_at, ahora) > SIN_SENAL_MIN;
      const color = d.turbo_route_id != null ? DRIVER_ESTADO[d.estado].color : sinSenal ? COLOR.gris : DRIVER_ESTADO[d.estado].color;
      const icon = iconoChofer(L, d, color, ofrecidos.has(d.driver_id));
      let m = marcadores.current.drivers.get(d.driver_id);
      const tip = `${nombre(d)} · ${d.vehiculo ?? d.tipo} · ${DRIVER_ESTADO[d.estado].label}${d.turbo_route_id ? ` · Turbo #${d.turbo_route_id}` : ""}${ofrecidos.has(d.driver_id) ? " · ofrecido ahora" : ""} · ${haceCuanto(d.recorded_at, ahora)}${sinSenal ? " (sin señal)" : ""}`;
      if (!m) {
        m = Lx.marker([d.lat, d.lng], { icon, zIndexOffset: 500 }).addTo(g.drivers);
        // Texto, no HTML: el nombre y el vehículo los escribe el chofer.
        m.bindTooltip(textTooltip(tip));
        m.on("click", () => {
          const modo = modoRef.current;
          if (modo) asignarRef.current(modo.turbo, d.driver_id); else setDriverSel(d.driver_id);
        });
        marcadores.current.drivers.set(d.driver_id, m);
      } else {
        // Como las tiendas: solo si se movió y solo si cambió el ícono (rehacerlo
        // en cada sondeo reconstruía el DOM y reiniciaba el halo del ofrecido).
        const ll = m.getLatLng();
        if (ll.lat !== d.lat || ll.lng !== d.lng) m.setLatLng([d.lat, d.lng]);
        if (m.options.icon?.options?.html !== icon.options.html) m.setIcon(icon);
        m.setTooltipContent?.(textTooltip(tip));
      }
      m.setOpacity?.(sinSenal && d.turbo_route_id == null ? 0.55 : 1);
    }
    for (const [id, m] of marcadores.current.drivers) if (!vivosD.has(id)) { (g.drivers as any).removeLayer(m); marcadores.current.drivers.delete(id); }
  }, [leafletMap, data, contenidoTienda]);

  const irATienda = (storeId: number | null) => {
    const s = data?.stores.find((x) => x.id === storeId);
    const mapa = leafletMap?.map;
    if (!s || !mapa) return;
    mapa.setView([s.lat, s.lng], Math.max(mapa.getZoom(), 14));
    abrirTienda(s);
  };

  /* ===== lista ===== */

  const turbos = data?.turbos ?? [];
  const visibles = useMemo(() => {
    const f = turbos.filter((t) => (!estadoFiltro || t.estado === estadoFiltro) && (!ocultarCerrados || !t.terminal || resaltados[t.delivery_id]));
    return f.sort((a, b) => {
      if (a.terminal !== b.terminal) return a.terminal ? 1 : -1;
      if (!a.terminal) {
        const oa = ORDEN_ESTADOS.indexOf(a.estado), ob = ORDEN_ESTADOS.indexOf(b.estado);
        if (oa !== ob) return oa - ob;
        return (a.seconds_to_deadline ?? 1e9) - (b.seconds_to_deadline ?? 1e9);
      }
      return new Date(b.estado_at ?? 0).getTime() - new Date(a.estado_at ?? 0).getTime();
    });
  }, [turbos, estadoFiltro, ocultarCerrados, resaltados]);

  const res = data?.resumen;
  const ahoraServidor = Date.now() + desfase;
  const esperando = data?.drivers.filter((d) => d.estado === "ESPERANDO" && minutosDesde(d.recorded_at, ahoraServidor) <= SIN_SENAL_MIN).length ?? 0;
  const llevando = data?.drivers.filter((d) => d.turbo_route_id != null).length ?? 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <style>{CSS}</style>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", flexWrap: "wrap", alignItems: "flex-start" }}>
          <div>
            <h2 style={titleStyle}>Turbo · En vivo</h2>
            <p style={subtitleStyle}>Las tiendas con Turbo posible, los choferes de Turbo y cada Turbo del día en un solo mapa. Entra un Turbo: la tienda se pone verde, se ven los anillos y a quién se le ofreció. Si nadie lo toma, la tienda se pone roja y desde ahí se asigna a mano. Se refresca sola cada {REFRESCO_MS / 1000} segundos.</p>
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
            <Button variant="secondary" className="tv-sonido" onClick={tocarSonido}>
              {!sonido ? "🔕 Sonido apagado" : sonidoEstado === "bloqueado" ? "🔔 Tocá para habilitar el sonido" : sonidoEstado === "sin-soporte" ? "🔕 Este navegador no suena" : "🔔 Sonido activado"}
            </Button>
            <Button variant="secondary" onClick={() => vivo.reload()}>Actualizar</Button>
            <Button variant="secondary" onClick={() => navigate("/admin/turbo/despachos")}>Despachos</Button>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "14px", marginTop: "18px" }}>
          {/* Los arrastrados de días anteriores siguen en la lista y en los otros números (están abiertos), pero no son «del día». */}
          <div><div style={smallMutedStyle}>Turbos del día</div><div style={kpiStyle}>{res ? res.total - (res.arrastrados ?? 0) : "—"}</div>{res?.arrastrados ? <div style={smallMutedStyle}>+{res.arrastrados} de días anteriores</div> : null}</div>
          <div><div style={smallMutedStyle}><span style={punto(TURBO_ESTADO_COLOR.SIN_CHOFER)} />Sin chofer</div><div style={{ ...kpiStyle, color: res?.sin_chofer ? COLOR.rojo : "var(--text-primary)" }}>{res?.sin_chofer ?? "—"}</div></div>
          <div><div style={smallMutedStyle}><span style={punto(TURBO_ESTADO_COLOR.CON_CHOFER)} />Con chofer</div><div style={kpiStyle}>{res?.con_chofer ?? "—"}</div></div>
          <div><div style={smallMutedStyle}><span style={punto(TURBO_ESTADO_COLOR.EN_CAMINO)} />En camino</div><div style={{ ...kpiStyle, color: res?.en_camino ? COLOR.verde : "var(--text-primary)" }}>{res?.en_camino ?? "—"}</div></div>
          <div><div style={smallMutedStyle}>Entregados</div><div style={kpiStyle}>{res?.entregados ?? "—"}</div>{res?.no_entregados ? <div style={{ ...smallMutedStyle, color: COLOR.rojo }}>{res.no_entregados} no entregados</div> : null}</div>
          <div><div style={smallMutedStyle}><span style={punto(COLOR.azul)} />Choferes esperando</div><div style={{ ...kpiStyle, color: esperando ? COLOR.azul : "var(--text-primary)" }}>{data ? esperando : "—"}</div><div style={smallMutedStyle}>{llevando} llevando un Turbo</div></div>
          <div><div style={smallMutedStyle}>Actualizado</div><div style={{ ...kpiStyle, fontSize: "18px" }}>{vivo.updatedAt ? <Hace iso={vivo.updatedAt.toISOString()} desfase={0} /> : "—"}</div><div style={smallMutedStyle}>{data?.ultimo_dato_ml ? <>último dato de ML <Hace iso={data.ultimo_dato_ml} desfase={desfase} /></> : `cada ${REFRESCO_MS / 1000} s`}</div></div>
        </div>
        {sonido && sonidoEstado === "bloqueado" ? <div role="status" style={{ marginTop: "10px", color: "var(--warning, #d29922)" }}>El sonido está prendido pero el navegador no lo deja sonar hasta que toques la página: tocá en cualquier lado para habilitar el sonido.</div> : null}
        {vivo.error ? <div style={{ marginTop: "10px", color: "var(--danger)" }}>{vivo.error}</div> : null}
        {error ? <div style={{ marginTop: "10px", color: "var(--danger)" }}>{error}</div> : null}
        {notice ? <div style={{ marginTop: "10px", color: "var(--success)" }}>{notice}</div> : null}
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(320px, 1fr)", gap: "20px" }}>
        <Card>
          {driverSel != null ? <FichaChofer driverId={driverSel} live={data} desfase={desfase} req={req} onCerrar={() => setDriverSel(null)} onAvisar={avisarChofer} avisando={avisando === driverSel} /> : null}
          {modoAsignar ? (
            <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap", marginBottom: "10px", padding: "8px 12px", borderRadius: "10px", border: `1px solid ${COLOR.violeta}`, background: "rgba(163,113,247,0.12)", color: "var(--text-primary)" }}>
              <span>Tocá un chofer en el mapa para darle el Turbo de <b>{modoAsignar.turbo.store_name}</b> → {modoAsignar.turbo.nombre_destinatario}.</span>
              <button type="button" style={linkBtnStyle} onClick={() => setModoAsignar(null)}>cancelar</button>
            </div>
          ) : null}
          <div ref={contenedorRef} style={{ height: "640px", borderRadius: "12px", overflow: "hidden", background: "var(--surface-2, #0c0e12)" }}>
            {!leaflet ? <div style={{ ...smallMutedStyle, padding: "16px" }}>Cargando el mapa…</div> : null}
          </div>
          <div style={{ ...smallMutedStyle, marginTop: "10px", display: "flex", gap: "14px", flexWrap: "wrap" }}>
            <span><i style={punto(COLOR.gris)} />tienda sin Turbo</span>
            <span><i style={punto(COLOR.verde)} />tienda con Turbo en curso</span>
            <span><i style={punto(COLOR.rojo)} />tienda con Turbo sin chofer (tocala para asignar)</span>
            <span>· moto o auto: </span>
            <span><i style={punto(COLOR.azul)} />esperando</span>
            <span><i style={punto(COLOR.naranja)} />yendo a retirar</span>
            <span><i style={punto(COLOR.verde)} />entregando</span>
            <span><i style={punto(COLOR.violeta)} />anillos y chofer al que se le está ofreciendo</span>
            <span>· gris: sin señal hace más de {SIN_SENAL_MIN} min o con el Turbo apagado</span>
          </div>
        </Card>

        <Card>
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", alignItems: "center", marginBottom: "10px" }}>
            <button type="button" style={chip(estadoFiltro === "")} onClick={() => setEstadoFiltro("")}>Todos</button>
            {ORDEN_ESTADOS.map((s) => <button key={s} type="button" style={chip(estadoFiltro === s, TURBO_ESTADO_COLOR[s])} onClick={() => setEstadoFiltro(estadoFiltro === s ? "" : s)}>{TURBO_ESTADO_LABEL[s]}</button>)}
            <label style={{ ...smallMutedStyle, display: "inline-flex", gap: "6px", alignItems: "center", cursor: "pointer" }}>
              <input id="turbo-vivo-ocultar" type="checkbox" checked={ocultarCerrados} onChange={(e) => { setOcultarCerrados(e.target.checked); guardarPref("turbo-vivo-ocultar", e.target.checked); }} /> ocultar cerrados
            </label>
          </div>
          <div style={{ maxHeight: "640px", overflowY: "auto" }}>
            {!data ? <div style={smallMutedStyle}>Cargando…</div> : null}
            {data && visibles.length === 0 ? <div style={smallMutedStyle}>{turbos.length ? "Nada con ese filtro." : "Ningún Turbo hoy todavía."}</div> : null}
            {visibles.map((t) => {
              const r = resaltados[t.delivery_id];
              const vencido = t.seconds_to_deadline != null && t.seconds_to_deadline < 0;
              const pronto = t.seconds_to_deadline != null && t.seconds_to_deadline >= 0 && t.seconds_to_deadline < 1800;
              const fondo = r ? (r.nuevo ? "rgba(79,140,255,0.14)" : "rgba(63,185,80,0.12)") : t.estado === "SIN_CHOFER" ? "rgba(248,81,73,0.06)" : "transparent";
              return (
                <div key={t.delivery_id} style={{ ...filaStyle, background: fondo, opacity: t.terminal && !r ? 0.7 : 1 }} onClick={() => irATienda(t.store_id)}>
                  <div><b style={{ color: "var(--text-primary)" }}>{fmtHoraTurbo(t, t.entro_at)}</b>{r?.nuevo ? <div style={{ ...smallMutedStyle, color: COLOR.azul, fontWeight: 700 }}>NUEVO</div> : null}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: "var(--text-primary)", fontSize: "14px", fontWeight: 600 }}>{t.store_name ?? "tienda"} <span style={{ ...smallMutedStyle, fontWeight: 400 }}>→</span> {t.nombre_destinatario ?? "—"}</div>
                    <div style={smallMutedStyle}>
                      <span style={badge(TURBO_ESTADO_COLOR[t.estado])}>{TURBO_ESTADO_LABEL[t.estado]}</span>
                      {" "}{t.chofer ? <button type="button" style={linkBtnStyle} onClick={(e) => { e.stopPropagation(); setDriverSel(t.chofer!.driver_id); }}>{nombre(t.chofer)}</button> : t.detalle}
                      {!t.terminal && t.vence_at ? <span style={{ color: vencido ? COLOR.rojo : pronto ? COLOR.amarillo : undefined }}> · vence {fmtHoraTurbo(t, t.vence_at)}{t.seconds_to_deadline != null ? ` (${vencido ? "vencido" : `faltan ${fmtMin(t.seconds_to_deadline)}`})` : ""}</span> : null}
                    </div>
                    {t.estado === "SIN_CHOFER" && !t.terminal ? (
                      <div style={{ marginTop: "4px", display: "flex", gap: "10px" }} onClick={(e) => e.stopPropagation()}>
                        <button type="button" style={linkBtnStyle} onClick={() => irATienda(t.store_id)}>asignar desde la tienda</button>
                        <button type="button" style={linkBtnStyle} onClick={() => setModoAsignar({ turbo: t })}>elegir chofer en el mapa</button>
                        {despachoVivoDe(t) ? (t.despacho!.status !== "OFFERING" ? <button type="button" style={linkBtnStyle} disabled={!!busy} onClick={() => reofertar(t)}>reofertar</button> : null)
                          : <button type="button" style={linkBtnStyle} disabled={!!busy} onClick={() => ofrecer(t)}>ofrecer a los choferes</button>}
                      </div>
                    ) : null}
                  </div>
                  <span style={smallMutedStyle}>{fmtTime(t.estado_at)}</span>
                </div>
              );
            })}
          </div>
          {busy ? <div style={{ ...smallMutedStyle, marginTop: "8px" }}>{busy}</div> : null}
        </Card>
      </div>
    </div>
  );
}
